/**
 * Lo que hizo la compra, apenas se carga.
 *
 * PEDIDO DEL LOCAL, 05/11/2026: «una vez termines, mostrá en un popup un
 * resumen de lo que se hizo». Antes quedaba un aviso de dos segundos («Entraron
 * 30 unidades») y para saber que precios cambiaron habia que abrir la compra.
 *
 * Lo arma la pantalla en el momento de «Cargar», con lo que se reviso en cada
 * renglon, y la base agrega lo que hizo (unidades, deuda). Desde aca se puede
 * deshacer: es el momento en que se ve si algo salio mal.
 */
import { Check, TriangleAlert, Undo2 } from "lucide-react";
import type { Moneda } from "@tora/shared";

import { mostrarCantidad } from "@/components/catalogo/Campos";
import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ResumenDeCarga {
  proveedor: string;
  referencia: string;
  /** Lo que se sumo al stock, producto por producto. */
  stock: Array<{ nombre: string; cantidad: number }>;
  /** Los precios de venta que cambian o nacen. `antes` null es un producto nuevo. */
  precios: Array<{ nombre: string; antes: number | null; ahora: number; moneda: Moneda }>;
  /** Cuantos quedaron con el precio de antes. */
  precioIgual: number;
  nuevos: Array<{ nombre: string; rubro: string | null }>;
  /** Lo que se vinculo a mano: la proxima vez ya viene vinculado. */
  aprendido: Array<{ remito: string; producto: string }>;
  /** Las lineas que se dejaron afuera del stock. */
  afuera: string[];
  /** Como se pago: «Le debés a Foxbox», «Salió de la caja de efectivo»… */
  plata: string;
  total: number | null;
  moneda: Moneda;
}

export function DialogResumenDeCarga({
  resumen,
  abierto,
  deshaciendo,
  onCerrar,
  onDeshacer,
}: {
  resumen: ResumenDeCarga | null;
  abierto: boolean;
  deshaciendo: boolean;
  onCerrar: () => void;
  onDeshacer: () => void;
}): JSX.Element {
  const unidades = resumen?.stock.reduce((t, s) => t + s.cantidad, 0) ?? 0;
  return (
    <Dialog open={abierto && resumen !== null} onOpenChange={(a) => (a ? null : onCerrar())}>
      <DialogContent tamano="md">
        {resumen ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="inline-flex size-6 items-center justify-center rounded-full bg-success text-success-foreground">
                  <Check className="size-4" aria-hidden="true" />
                </span>
                Compra cargada
              </DialogTitle>
              <DialogDescription>
                {resumen.proveedor} · {resumen.referencia}
              </DialogDescription>
            </DialogHeader>

            <DialogBody className="space-y-4">
              <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Cifra
                  titulo="Entraron al stock"
                  valor={`${mostrarCantidad(unidades)} u.`}
                  pie={`${resumen.stock.length} ${resumen.stock.length === 1 ? "producto" : "productos"}`}
                />
                <Cifra
                  titulo="Precios de venta"
                  valor={String(resumen.precios.length)}
                  pie={
                    resumen.precios.length === 1 ? "cambió o es nuevo" : "cambiaron o son nuevos"
                  }
                />
                <div className="space-y-0.5 rounded-md bg-muted px-3 py-2">
                  <dt className="text-xs text-muted-foreground">{resumen.plata}</dt>
                  <dd className="text-lg font-semibold">
                    {resumen.total === null ? (
                      "—"
                    ) : (
                      <Importe monto={resumen.total} moneda={resumen.moneda} />
                    )}
                  </dd>
                </div>
              </dl>

              {resumen.stock.length > 0 ? (
                <Lista titulo="Stock">
                  {resumen.stock.map((s, i) => (
                    <Par key={i} izquierda={s.nombre} derecha={`+${mostrarCantidad(s.cantidad)}`} />
                  ))}
                </Lista>
              ) : null}

              <Lista titulo="Precios de venta">
                {resumen.precios.length === 0 ? (
                  <li className="text-muted-foreground">Ningún precio cambió.</li>
                ) : (
                  resumen.precios.map((p, i) => (
                    <Par
                      key={i}
                      izquierda={p.nombre}
                      derecha={
                        <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
                          {p.antes === null ? (
                            <Badge variant="info">nuevo</Badge>
                          ) : (
                            <>
                              <Importe
                                monto={p.antes}
                                moneda={p.moneda}
                                tamano="sm"
                                className="text-muted-foreground line-through"
                              />
                              <span className="text-muted-foreground">→</span>
                            </>
                          )}
                          <Importe monto={p.ahora} moneda={p.moneda} className="font-semibold" />
                        </span>
                      }
                    />
                  ))
                )}
                {resumen.precioIgual > 0 ? (
                  <li className="text-xs text-muted-foreground">
                    {resumen.precioIgual === 1
                      ? "1 producto quedó con el precio de antes."
                      : `${resumen.precioIgual} productos quedaron con el precio de antes.`}
                  </li>
                ) : null}
              </Lista>

              {resumen.nuevos.length > 0 ? (
                <Lista titulo="Productos nuevos en el catálogo">
                  {resumen.nuevos.map((n, i) => (
                    <Par key={i} izquierda={n.nombre} derecha={n.rubro ?? ""} gris />
                  ))}
                </Lista>
              ) : null}

              {resumen.aprendido.length > 0 ? (
                <Lista titulo="Quedó aprendido para la próxima">
                  {resumen.aprendido.map((a, i) => (
                    <Par key={i} izquierda={`«${a.remito}»`} derecha={a.producto} />
                  ))}
                </Lista>
              ) : null}

              {resumen.afuera.length > 0 ? (
                <Aviso tono="warning" compacto>
                  <span className="inline-flex items-start gap-2">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    <span>No entraron al stock: {resumen.afuera.join(", ")}.</span>
                  </span>
                </Aviso>
              ) : null}
            </DialogBody>

            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="ghost" disabled={deshaciendo} onClick={onDeshacer}>
                <Undo2 aria-hidden="true" />
                Deshacer la carga
              </Button>
              <Button type="button" onClick={onCerrar} autoFocus>
                Listo
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function Cifra({
  titulo,
  valor,
  pie,
}: {
  titulo: string;
  valor: string;
  pie: string;
}): JSX.Element {
  return (
    <div className="space-y-0.5 rounded-md bg-muted px-3 py-2">
      <dt className="text-xs text-muted-foreground">{titulo}</dt>
      <dd className="text-lg font-semibold tabular-nums">{valor}</dd>
      <dd className="text-xs text-muted-foreground">{pie}</dd>
    </div>
  );
}

function Lista({ titulo, children }: { titulo: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-1">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {titulo}
      </h3>
      <ul className="divide-y divide-border text-sm">{children}</ul>
    </section>
  );
}

function Par({
  izquierda,
  derecha,
  gris,
}: {
  izquierda: React.ReactNode;
  derecha: React.ReactNode;
  gris?: boolean;
}): JSX.Element {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-1">
      <span className="min-w-0">{izquierda}</span>
      <span className={gris ? "text-muted-foreground" : "tabular-nums"}>{derecha}</span>
    </li>
  );
}
